import { Box, Checkbox, Theme, Tooltip } from "@material-ui/core";
import { Info } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { YZTypography } from "@yardzen-inc/react-common";
import { FC, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { addToAddOns, removeFromAddOns } from "../../slices/productSlice";
import { ProductName } from "../../types/Products";
import { hasAddOnExterior } from "../../util/functions/hasAddOn";

export const ExteriorUpsell: FC<{ packageName: string }> = ({
  packageName,
}) => {
  const useStyles = makeStyles((theme: Theme) => ({
    exteriorUpsellWrapper: {
      width: "102%",
      backgroundColor: "#F8F7F6",
      borderRadius: 10,
      display: "flex",
      flexFlow: "row",
      alignItems: "flex-start",
      padding: 10,
      marginBottom: 10,
    },
    priceWrapper: {
      width: "30%",
      justifyContent: "flex-end",
      alignItems: "center",
      display: "flex",
      height: 50,
      [theme.breakpoints.down("sm")]: {
        width: "15%",
      },
    },
    invisible: {
      visibility: "hidden",
    },
    upsellText: {
      width: "70%",
      paddingTop: 10,
      [theme.breakpoints.down("sm")]: {
        width: "85%",
      },
    },
    boldText: {
      fontWeight: "bold",
    },
    infoIcon: {
      fontSize: "1rem",
      verticalAlign: "middle",
    },
  }));
  const classes = useStyles();

  const getPackageType = (
    packageName: string
  ): "ExteriorUpsellFront" | "ExteriorUpsellBack" | "ExteriorUpsellFull" => {
    switch (packageName) {
      case "Front Yard Package":
        return "ExteriorUpsellFront";
      case "Backyard Package":
        return "ExteriorUpsellBack";
      case "Full Yard Package":
        return "ExteriorUpsellFull";
      default:
        return "ExteriorUpsellBack";
    }
  };

  const fallBackPricing = {
    ExteriorUpsellFront: 60000,
    ExteriorUpsellBack: 70000,
    ExteriorUpsellFull: 80000,
  };

  const { products, addOnSkus } = useAppSelector(state => state.products);
  const { restrictChanges } = useAppSelector(state => state.checkout);
  const dispatch = useAppDispatch();
  const [showExteriorUpsell, setShowExteriorUpsell] = useState(
    addOnSkus.findIndex(addOn => hasAddOnExterior({ addOn })) >= 0
  );
  const currentPackage = getPackageType(packageName);

  const exteriorPriceObject = products.find(
    product => product.name === ProductName[currentPackage]
  );

  const exteriorUpsellPrice = (
    (exteriorPriceObject?.skus.data[0].price ||
      fallBackPricing[currentPackage]) / 100
  ).toFixed(0);

  const handleChange = () => {
    if (!showExteriorUpsell) {
      exteriorPriceObject &&
        dispatch(addToAddOns({ addOn: exteriorPriceObject?.skus.data[0] }));
    } else {
      exteriorPriceObject &&
        dispatch(
          removeFromAddOns({ addOn: exteriorPriceObject?.skus.data[0] })
        );
    }
    setShowExteriorUpsell(!showExteriorUpsell);
  };

  const displayPrice = () => {
    if (!showExteriorUpsell) return null;

    return (
      <YZTypography style={{ fontSize: "1.2rem" }}>
        ${exteriorUpsellPrice}
      </YZTypography>
    );
  };

  const hideComponent = restrictChanges && !showExteriorUpsell;

  return (
    <Box
      className={`${classes.exteriorUpsellWrapper} ${
        hideComponent ? classes.invisible : null
      }`}
    >
      <Checkbox
        id="exterior-checkbox"
        checked={showExteriorUpsell}
        onChange={handleChange}
        disabled={restrictChanges}
        inputProps={{
          "aria-label": "add exterior service checkbox",
        }}
      />

      <>
        <YZTypography
          color="textSecondary"
          variant="body2"
          className={classes.upsellText}
        >
          +${exteriorUpsellPrice}, reimagine your home's facade with new paint,
          windows, doors, and more with an{" "}
          <span className={classes.boldText}> exterior upgrade</span>{" "}
          <Tooltip
            title="Elevate the aesthetic of your home's exterior, creating a cohesive home facade to compliment your new landscaping. Includes the ability to see your home with new paint, windows, doors, lighting & decor, and exterior additions."
            leaveTouchDelay={10000}
            enterTouchDelay={0}
          >
            <Info className={classes.infoIcon} data-testid="exterior-tooltip" />
          </Tooltip>
        </YZTypography>
        <Box className={classes.priceWrapper}>{displayPrice()}</Box>
      </>
    </Box>
  );
};
